import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Check, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { api } from '../../api/client';
import { toast } from 'sonner';
import { RENTAL_FAMILY_OPTIONS } from '../../lib/rental-inspections';

interface Category {
  id: string;
  name: string;
  display_order: number;
  active: boolean;
  /** api#861. NULL = the base inspection checklist only. */
  checklist_family?: string | null;
}

export default function ManageCategoriesDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editOrder, setEditOrder] = useState(0);

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['rentals', 'asset-categories'],
    queryFn: () =>
      api.rentals.assetCategories.list().then(r => {
        const d = r.data?.data ?? r.data ?? [];
        return Array.isArray(d) ? d : [];
      }),
    enabled: open,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['rentals', 'asset-categories'] });

  const renameMutation = useMutation({
    mutationFn: ({ id, name, display_order }: { id: string; name: string; display_order: number }) =>
      api.rentals.assetCategories.update(id, { name, display_order }),
    onSuccess: () => {
      invalidate();
      setEditId(null);
      toast.success('Category updated');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to update'),
  });

  /**
   * Which inspection checklist this category's assets get: the base list, or
   * the base list plus a family's items (api#861). Saved on change; the API
   * allows it to admins and says so when it refuses.
   */
  const familyMutation = useMutation({
    mutationFn: ({ id, family }: { id: string; family: string | null }) =>
      api.rentals.assetCategories.update(id, { checklist_family: family }),
    onSuccess: () => {
      invalidate();
      toast.success('Inspection checklist updated');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to update the checklist'),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => api.rentals.assetCategories.update(id, { active: false }),
    onSuccess: () => {
      invalidate();
      toast.success('Category archived');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to archive'),
  });

  const sorted = [...categories].sort((a: Category, b: Category) => (a.display_order ?? 0) - (b.display_order ?? 0));

  const inputCls = 'bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:border-fieldiq-accent/50';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Manage Asset Categories</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-fieldiq-accent" />
          </div>
        ) : sorted.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No categories yet. Create one from the Add Asset form.</p>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider border-b border-border">
                  <th className="text-left py-2 px-2">Name</th>
                  <th className="text-left py-2 px-2 w-16">Order</th>
                  <th className="text-left py-2 px-2 w-16">Active</th>
                  <th className="text-left py-2 px-2">Inspection checklist</th>
                  <th className="text-right py-2 px-2 w-28">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((cat: Category) => (
                  <tr key={cat.id} className={`border-b border-border/50 ${!cat.active ? 'opacity-50' : ''}`}>
                    {editId === cat.id ? (
                      <>
                        <td className="py-2 px-2">
                          <input
                            className={inputCls + ' w-full'}
                            value={editName}
                            onChange={e => setEditName(e.target.value)}
                          />
                        </td>
                        <td className="py-2 px-2">
                          <input
                            type="number"
                            className={inputCls + ' w-14'}
                            value={editOrder}
                            onChange={e => setEditOrder(parseInt(e.target.value) || 0)}
                          />
                        </td>
                        <td className="py-2 px-2">
                          <span className={cat.active ? 'text-green-400' : 'text-muted-foreground'}>
                            {cat.active ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="py-2 px-2">
                          <select
                            aria-label={`Inspection checklist for ${cat.name}`}
                            className={inputCls}
                            value={cat.checklist_family ?? ''}
                            disabled={familyMutation.isPending}
                            onChange={(e) => familyMutation.mutate({ id: cat.id, family: e.target.value || null })}
                          >
                            <option value="">Base list only</option>
                            {RENTAL_FAMILY_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                          </select>
                        </td>
                        <td className="py-2 px-2 text-right">
                          <button
                            onClick={() => renameMutation.mutate({ id: cat.id, name: editName.trim(), display_order: editOrder })}
                            disabled={!editName.trim() || renameMutation.isPending}
                            className="text-green-400 hover:text-green-300 mr-2 disabled:opacity-50"
                          >
                            <Check size={14} />
                          </button>
                          <button onClick={() => setEditId(null)} className="text-muted-foreground hover:text-white">
                            <X size={14} />
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-2 px-2 text-white">{cat.name}</td>
                        <td className="py-2 px-2 text-muted-foreground font-mono">{cat.display_order ?? 0}</td>
                        <td className="py-2 px-2">
                          <span className={cat.active ? 'text-green-400' : 'text-muted-foreground'}>
                            {cat.active ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="py-2 px-2">
                          <select
                            aria-label={`Inspection checklist for ${cat.name}`}
                            className={inputCls}
                            value={cat.checklist_family ?? ''}
                            disabled={familyMutation.isPending}
                            onChange={(e) => familyMutation.mutate({ id: cat.id, family: e.target.value || null })}
                          >
                            <option value="">Base list only</option>
                            {RENTAL_FAMILY_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                          </select>
                        </td>
                        <td className="py-2 px-2 text-right">
                          <button
                            onClick={() => { setEditId(cat.id); setEditName(cat.name); setEditOrder(cat.display_order ?? 0); }}
                            className="text-fieldiq-accent hover:text-white text-xs mr-2"
                          >
                            Rename
                          </button>
                          {cat.active && (
                            <button
                              onClick={() => {
                                if (window.confirm(`Archive "${cat.name}"? It will be hidden from the category dropdown.`)) {
                                  archiveMutation.mutate(cat.id);
                                }
                              }}
                              className="text-red-400 hover:text-red-300 text-xs"
                            >
                              Archive
                            </button>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
